import { Play, Pencil, Trash2, Star } from "lucide-react";
import type { SavedFilter } from "../../services/admin/leads.admin.service";

interface SavedFiltersSidebarProps {
  filters: SavedFilter[];
  loading: boolean;
  onLoad: (filter: SavedFilter) => void;
  onRun: (filter: SavedFilter) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string, isDefault: boolean) => void;
}

export function SavedFiltersSidebar({
  filters,
  loading,
  onLoad,
  onRun,
  onDelete,
  onSetDefault,
}: SavedFiltersSidebarProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground px-1">
          Saved Filters
        </h3>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-20 bg-muted rounded-xl animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (filters.length === 0) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground px-1">
          Saved Filters
        </h3>
        <div className="text-center py-8 text-muted-foreground text-sm">
          <p>No saved filters yet.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Save a filter to reuse it later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground px-1">
        Saved Filters ({filters.length})
      </h3>
      <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
        {filters.map((filter) => (
          <div
            key={filter.id}
            className="border border-border rounded-xl p-3 hover:border-border transition-colors cursor-pointer group"
            onClick={() => onLoad(filter)}
          >
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-medium text-foreground truncate">
                  {filter.name}
                </span>
                {filter.isDefault && (
                  <Star
                    size={12}
                    className="text-[#d7785b] flex-shrink-0"
                    fill="#d7785b"
                  />
                )}
              </div>
            </div>

            {filter.description && (
              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                {filter.description}
              </p>
            )}

            {filter.lastUsedAt && (
              <p className="text-[10px] text-muted-foreground mb-2">
                Last used:{" "}
                {new Date(filter.lastUsedAt).toLocaleDateString()}
              </p>
            )}

            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRun(filter);
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-md transition-colors"
                style={{ backgroundColor: "#d7785b" }}
                title="Run this filter"
              >
                <Play size={10} />
                Run
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onLoad(filter);
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-muted-foreground bg-muted rounded-md hover:bg-muted transition-colors"
                title="Edit filter"
              >
                <Pencil size={10} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSetDefault(filter.id, !filter.isDefault);
                }}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                  filter.isDefault
                    ? "text-[#d7785b] bg-orange-50 dark:bg-orange-500/10"
                    : "text-muted-foreground bg-muted hover:bg-muted"
                }`}
                title={
                  filter.isDefault ? "Remove default" : "Set as default"
                }
              >
                <Star size={10} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm("Delete this saved filter?")) {
                    onDelete(filter.id);
                  }
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 rounded-md hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
                title="Delete filter"
              >
                <Trash2 size={10} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
